import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Loader2, Car, Calendar, Hash, Gauge, Users, MapPin, User, Radio, Wrench, ClipboardList } from "lucide-react";
import { updateVehicle } from "@/lib/api";
import { notifyNavixyPush } from "@/lib/navixyFeedback";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { fmtKm, dateFr } from "@/lib/format";
import { Stat, SectionCard, FormRow } from "@/components/Field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProvenanceSection from "@/components/ProvenanceSection";
import TelematicsCard from "@/components/tabs/TelematicsCard";

const GEN_FIELDS = ["plaque", "marque", "modele", "annee", "vin", "kilometrage", "groupe", "base", "responsable", "tracker_gps", "prochaine_maintenance", "prochaine_expertise"];
const pick = (v) => Object.fromEntries(GEN_FIELDS.map((k) => [k, v[k] ?? ""]));

export default function GeneralTab({ vehicle, onSaved }) {
  const { user } = useAuth();
  const readOnly = !can(user, "vehicles.edit");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(() => pick(vehicle));
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const startEdit = () => {
    setForm(pick(vehicle));
    setEdit(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateVehicle(vehicle.id, {
        ...form,
        annee: Number(form.annee) || 0,
        kilometrage: Number(form.kilometrage) || 0,
        prochaine_maintenance: form.prochaine_maintenance || null,
        prochaine_expertise: form.prochaine_expertise || null,
      });
      toast.success("Informations enregistrées");
      notifyNavixyPush(res?.navixy_push, vehicle.id);
      onSaved?.();
      setEdit(false);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (edit) {
    return (
      <SectionCard title="Modifier les informations générales" testId="general-edit">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            ["plaque", "Plaque"], ["vin", "VIN"], ["marque", "Marque"], ["modele", "Modèle"],
            ["annee", "Année", "number"], ["kilometrage", "Kilométrage", "number"],
            ["groupe", "Groupe"], ["base", "Base"], ["responsable", "Responsable"], ["tracker_gps", "Tracker GPS"],
            ["prochaine_maintenance", "Prochaine maintenance", "date"], ["prochaine_expertise", "Prochaine expertise", "date"],
          ].map(([k, label, type]) => (
            <FormRow key={k} label={label}>
              <Input data-testid={`gen-${k}`} type={type || "text"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
            </FormRow>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEdit(false)}>Annuler</Button>
          <Button data-testid="general-save" onClick={save} disabled={saving} className="gap-2 bg-slate-900 hover:bg-slate-800">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </Button>
        </div>
      </SectionCard>
    );
  }

  return (
    <div className="space-y-5">
      <SectionCard
        title="Informations générales"
      description="Identité administrative du véhicule"
      testId="general-view"
      action={!readOnly && <Button variant="outline" size="sm" onClick={startEdit} data-testid="general-edit-btn" className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Modifier</Button>}
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Marque" value={vehicle.marque} icon={Car} />
        <Stat label="Modèle" value={vehicle.modele} icon={Car} />
        <Stat label="Année" value={vehicle.annee || "—"} icon={Calendar} />
        <Stat label="VIN" value={vehicle.vin} icon={Hash} />
        <Stat label="Kilométrage" value={fmtKm(vehicle.kilometrage)} icon={Gauge} />
        <Stat label="Groupe" value={vehicle.groupe} icon={Users} />
        <Stat label="Base" value={vehicle.base} icon={MapPin} />
        <Stat label="Responsable" value={vehicle.responsable} icon={User} />
        <Stat label="Tracker GPS" value={vehicle.tracker_gps} icon={Radio} />
        <Stat label="Prochaine maintenance" value={dateFr(vehicle.prochaine_maintenance)} icon={Wrench} />
        <Stat label="Prochaine expertise" value={dateFr(vehicle.prochaine_expertise)} icon={ClipboardList} />
      </div>
      </SectionCard>
      <TelematicsCard vehicleId={vehicle.id} />
      <ProvenanceSection vehicleId={vehicle.id} />
    </div>
  );
}
